/* eslint-disable max-lines */
import { ArticleType } from '@wix/answers-api';
import { Category } from '@wix/answers-api/dist/kb';
import { clickOnAnswer } from '@wix/bi-logger-new-help-center/v2';
import {
  Box,
  Divider,
  Heading,
  IconButton,
  Item,
  Loader,
  Modal,
  Pagination,
  Tabs,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import { FunctionComponent, useContext, useState } from 'react';
import { animateScroll } from 'react-scroll';
import ChangeOrderSmall from 'wix-ui-icons-common/ChangeOrderSmall';
import { BI, EXPERIMENTS, LOCALES, SearchResultsFilter } from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { useBI } from '../../hooks/useBI';
import { SEARCH_RESULTS_PAGE_SIZE } from '../../pages/search';
import { ArticleSearchResult, ArticleSearchResultItem } from '../../types';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { COLORS } from '../../utils/theme';
import { Breadcrumbs } from '../Breadcrumbs';
import { RenderHTML } from '../RenderHTML';
import { FilterModal } from './FilterModal';
import { SearchResultItem } from './SearchResultItem';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { pageClick } from '@wix/bi-logger-customer-care-data/v2';
import { isMobile } from '../../utils/userAgent';
import { Context } from '../../context';
import css from './index.module.scss';

export const getSearchResultsFilterItems = (
  locale?: string
): Readonly<SearchResultsFilter[]> => {
  const basic = [
    SearchResultsFilter.ALL,
    SearchResultsFilter.ARTICLES,
    SearchResultsFilter.KNOWN_ISSUES,
  ];

  return locale && locale !== LOCALES.EN
    ? [...basic, SearchResultsFilter.FEATURE_REQUESTS]
    : [...basic, SearchResultsFilter.ROADMAP];
};

export type ArticleTypesCount = Record<
  Exclude<ArticleType, ArticleType.VIDEO>,
  number
>;

export const mapArticleTypeToEnum = {
  [SearchResultsFilter.ARTICLES]: ArticleType.ARTICLE,
  [SearchResultsFilter.KNOWN_ISSUES]: ArticleType.KNOWN_ISSUE,
  [SearchResultsFilter.ROADMAP]: ArticleType.FEATURE_REQUEST,
  [SearchResultsFilter.FEATURE_REQUESTS]: ArticleType.FEATURE_REQUEST,
} as const;

export type SearchResultsProps = {
  term: string;
  searchResults: ArticleSearchResult;
  articlesTypesCount: ArticleTypesCount;
  categoriesPaths: Record<string, Category[]>;
  currentPage: number;
  activatedTab: SearchResultsFilter;
  onPageChange: (page: number) => void;
  onFilterChange: (filter: SearchResultsFilter) => void;
  isLoading: boolean;
};

export const SearchResults: FunctionComponent<SearchResultsProps> = ({
  term,
  searchResults,
  articlesTypesCount,
  categoriesPaths,
  currentPage,
  activatedTab,
  onPageChange,
  onFilterChange,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { itemsCount, items } = searchResults;
  const { experiments } = useExperiments({ readOnly: true });
  const context = useContext(Context);
  const isMobileView = isMobile(context);
  const isKASearchEnabled =
    experiments.enabled(EXPERIMENTS.SPECS.KA_SEARCH) && locale === LOCALES.EN;

  const onChangeTab = async (tab: Item) => {
    if (tab.id !== activatedTab) {
      onFilterChange(`${tab.id}` as SearchResultsFilter);
      await sendBIEvent(
        pageClick({
          source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
          kb_lang: locale as string,
          clicked_item_type: BI.CLICKED_ITEM_TYPES.TAB_SELECTION,
          clicked_text: tab.title as string,
          clicked_item_order: `${
            getSearchResultsFilterItems(locale).indexOf(
              tab.id as SearchResultsFilter
            ) + 1
          }`,
        })
      );
    }
  };

  const onPageChangeEvent = async (page: number) => {
    animateScroll.scrollToTop();
    onPageChange(page);
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.PAGE_SELECTION,
        clicked_url: `${window.location.origin}/search&page=${page}`,
        clicked_text: `${page}`,
      })
    );
  };

  const onArticleTopicBadgeClick = async (
    topic: Category,
    itemOrder: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.CATEGORY_SELECTION,
        clicked_url: topic.url,
        clicked_text: topic.name,
        clicked_item_order: `${itemOrder}`,
        clicked_item_id: topic.id,
      })
    );
  };

  const onArticleResolutionBadgeClick = async (
    clickedText: string,
    url: string,
    itemOrder: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.TAG_SELECTION,
        clicked_text: clickedText,
        clicked_url: url,
        clicked_item_order: `${itemOrder}`,
      })
    );
  };

  const onArticleTypeTagClick = async (
    clickedText: string,
    url: string,
    itemOrder: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.SOURCE_HOMEPAGE_SELECTION,
        clicked_text: clickedText,
        clicked_url: url,
        clicked_item_order: `${itemOrder}`,
      })
    );
  };

  const onArticleClick = async (
    article: ArticleSearchResultItem,
    itemOrder: number
  ) => {
    await sendBIEvent(
      clickOnAnswer({
        source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
        kb_lang: locale as string,
        clicked_url: article.url,
        clicked_item_id: article.id,
        clicked_item_order: `${itemOrder}`,
        question: term,
        clicked_item_type: mapArticleTypeToItemType(article.type),
        search_result_selection:
          BI.SEARCH_RESULT_SELECTION.FULL_SEARCH_RESULTS_LIST,
        page_number: currentPage,
        search_method: searchResults.searchMethod,
        ...(article?.distance
          ? { output_distance_str: `${article.distance}` }
          : {}),
      })
    );
  };

  const numPages = Math.ceil(itemsCount / SEARCH_RESULTS_PAGE_SIZE);

  const breadcrumbItems = [
    { id: 'search-results', label: t('search-results-page.breadcrumb') },
  ];

  const allItemsCount =
    articlesTypesCount[ArticleType.ARTICLE] +
    articlesTypesCount[ArticleType.KNOWN_ISSUE] +
    articlesTypesCount[ArticleType.FEATURE_REQUEST];

  const resultsTitle =
    itemsCount >= 0
      ? t('search-results-page.title', { count: allItemsCount, term })
      : t('search-results-page.title-single', { term });

  return (
    <Box
      direction="vertical"
      className={css.searchResultsWrapper}
      dataHook={DATA_HOOKS.SEARCH_RESULTS}
    >
      <Breadcrumbs breadcrumbItems={breadcrumbItems} shallow={false} />
      <Box direction="vertical" className={css.searchResults}>
        <Box
          className={css.searchResultsHeader}
          verticalAlign="middle"
          align="space-between"
        >
          <Heading
            appearance="H3"
            ellipsis
            dataHook={DATA_HOOKS.SEARCH_RESULTS_TITLE}
            className={css.searchResultsTitle}
          >
            <RenderHTML
              html={resultsTitle}
              className={css.searchResultsTitleHTML}
            />
          </Heading>

          <IconButton
            dataHook={DATA_HOOKS.MOBILE_SORT_ICON_BUTTON}
            onClick={() => setIsModalOpen(true)}
            className={css.modalButton}
            priority="secondary"
            size={isMobileView ? 'tiny' : 'small'}
          >
            <ChangeOrderSmall
              height={'11px'}
              width={'11px'}
              color={COLORS.B10}
            />
          </IconButton>
        </Box>
        <Tabs
          className={`${css.searchResultsTabs}`}
          activeId={activatedTab}
          dataHook={DATA_HOOKS.SEARCH_RESULTS_TABS}
          onClick={onChangeTab}
          items={getSearchResultsFilterItems(locale).map((item) => ({
            dataHook:
              item === activatedTab
                ? DATA_HOOKS.SEARCH_RESULTS_TAB_ACTIVE
                : DATA_HOOKS.SEARCH_RESULTS_TAB,
            id: item,
            title: t(`search-results-page.tabs.${item}`, {
              count:
                item === SearchResultsFilter.ALL
                  ? allItemsCount
                  : articlesTypesCount[mapArticleTypeToEnum[item]],
            }),
          }))}
        />
        {isLoading && isKASearchEnabled ? (
          <Box align="center" className={css.loaderWrapper}>
            <Loader />
          </Box>
        ) : (
          <Box direction="vertical" className={css.searchResultsList}>
            {items.length > 0 ? (
              items.map((searchResult, idx) => (
                <>
                  <SearchResultItem
                    key={searchResult.id}
                    rootTopic={categoriesPaths?.[searchResult.categoryId]?.[0]}
                    onArticleResolutionBadgeClick={(
                      clickedText: string,
                      url: string
                    ) =>
                      onArticleResolutionBadgeClick(clickedText, url, idx + 1)
                    }
                    onArticleTypeTagClick={(clickedText: string, url: string) =>
                      onArticleTypeTagClick(clickedText, url, idx + 1)
                    }
                    onArticleTopicBadgeClick={(topic) =>
                      onArticleTopicBadgeClick(topic, idx + 1)
                    }
                    onArticleClick={() => onArticleClick(searchResult, idx + 1)}
                    {...searchResult}
                  />
                  {idx < items.length - 1 ? <Divider /> : null}
                </>
              ))
            ) : (
              <Box
                direction="vertical"
                align="center"
                className={css.noResultsForFilter}
                dataHook={DATA_HOOKS.SEARCH_RESULTS_NO_RESULTS_FOR_FILTER}
              >
                <Heading
                  appearance="H3"
                  className={css.noResultsForFilterTitle}
                >
                  {t(
                    'search-results-page.no-results-found-under-filter.title',
                    {
                      term,
                    }
                  )}
                </Heading>
                <Text
                  size="medium"
                  className={css.noResultsForFilterDescription}
                >
                  {t(
                    'search-results-page.no-results-found-under-filter.description'
                  )}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>
      {!isLoading && itemsCount > SEARCH_RESULTS_PAGE_SIZE ? (
        <Pagination
          className={css.pagination}
          onChange={(e) => onPageChangeEvent(e.page)}
          currentPage={currentPage}
          totalPages={numPages}
          dataHook={DATA_HOOKS.SEARCH_RESULTS_PAGINATION}
        />
      ) : null}
      {!isLoading && (
        <Modal
          dataHook={DATA_HOOKS.FILTER_AND_SORT_MODAL}
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          screen="mobile"
          zIndex={99999}
          shouldCloseOnOverlayClick
        >
          <FilterModal
            articlesTypesCount={articlesTypesCount}
            activeFilter={activatedTab}
            locale={locale}
            onFilterChange={onFilterChange}
            onClose={() => setIsModalOpen(false)}
          />
        </Modal>
      )}
    </Box>
  );
};
